import { cn } from 'lib/helpers/utility';

import Icon from 'components/shared/Icon/Icon';
import Button from 'components/shared/Button/Button';
import Divider from 'components/shared/Divider/Divider';
import Container from 'components/shared/Container/Container';
import Typography from 'components/shared/Typography/Typography';
import ProgressBar from 'components/shared/ProgressBar/ProgressBar';

import CheckinProtections from './CheckinProtections';
import CheckinEstimateCard from './CheckinEstimateCard';
import CheckinSidebarPreview from './CheckinSidebarPreview';
import CheckinPaymentOptions from './CheckinPaymentOptions';

// The card-on-file notice is warning-colored on the stock Myriad look and follows the brand otherwise.
const CheckinPreview = ({ isDefaultBrand = false, layout = {} }) => {
  const { isNarrow, isTiny } = layout;
  const sectionPadding = isNarrow ? 'px-4' : 'px-10';

  return (
    // Mirrors `StepperLayout`: gradient shell, 320px sidebar and the shared Container for the step.
    <div
      className={cn(
        'rounded-3xl bg-gradient-to-b from-primary-50 to-primary-200',
        isNarrow ? 'p-6 lg:p-3' : 'p-10 lg:p-6'
      )}>
      <div className="relative flex justify-center gap-6">
        {!isNarrow && <CheckinSidebarPreview />}
        <Container className="mx-0 flex min-w-0 flex-col overflow-hidden rounded-xl bg-white">
          <ProgressBar progress={62} className="rounded-none" />

          <div
            className={cn(
              'relative flex gap-5 pb-5 pr-12 pt-6',
              sectionPadding,
              isTiny && 'flex-col gap-3'
            )}>
            <Icon icon="illustration-dr-clearcare" size={55} className="shrink-0" />
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <Typography variant="h5" weight="medium">
                Choose payment option
              </Typography>
              <Typography variant="p" size="small" className="block">
                To better serve our patients, we've invested in transparency and expanded our
                payment options. Please choose a payment option below for your estimated balance
                today. <span className="font-medium">Payment is required prior to treatment.</span>
              </Typography>
            </div>
            <Icon icon="close-mono" color="neutral" className="absolute right-6 top-6" />
          </div>
          <div className={sectionPadding}>
            <Divider />
          </div>

          <div className={cn('flex flex-col gap-6 py-5', sectionPadding)}>
            <CheckinEstimateCard isNarrow={isNarrow} />
            <Divider />
            <div
              className={cn(
                'flex items-start gap-2 rounded-lg border border-solid p-4',
                isDefaultBrand
                  ? 'border-warning-200 bg-warning-25'
                  : 'border-primary-200 bg-primary-25'
              )}>
              <Icon
                icon="info-filled"
                color={isDefaultBrand ? 'warning' : 'primary'}
                size={20}
                className="mt-0.5"
              />
              <div className="flex flex-col gap-1">
                <Typography variant="label" size="medium" weight="medium">
                  A card on file is required for every option below
                </Typography>
                <Typography variant="p" size="small" color="neutral-700">
                  Securely stored, and charged only under the authorization you sign.
                </Typography>
              </div>
            </div>
            <CheckinPaymentOptions isNarrow={isNarrow} />
            <CheckinProtections isNarrow={isNarrow} isTiny={isTiny} />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-0 border-t border-solid border-neutral-200 px-4 py-3">
            <div className="flex flex-wrap items-center gap-3">
              <span className="flex h-10 items-center justify-center gap-2 rounded-lg border border-solid border-primary-200 bg-gradient-to-r from-primary-50 to-primary-10 px-4 text-sm font-medium leading-6 text-primary-500">
                <Icon icon="illustration-dr-clearcare-2" size={28} />
                Dr.ClearCare
              </span>
              <Button
                variant="tertiary"
                color="neutral"
                icon="editor-firstline3rec-mono"
                text="Good Faith Estimate"
                tabIndex={-1}
              />
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-3">
              <Button variant="tertiary" color="neutral" text="Back" tabIndex={-1} />
              <Button text="Acknowledge & continue" disabled tabIndex={-1} />
            </div>
          </div>
        </Container>
      </div>
    </div>
  );
};

export default CheckinPreview;
